import { useState, useMemo } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "./button";
import { DataTablePagination } from "./data-table-pagination";
import { cn } from "@/lib/utils";

// Column definition type
export interface ColumnDef<T> {
  id: string;
  header: string;
  accessorKey?: keyof T;
  cell?: (row: T) => React.ReactNode;
  className?: string;
  headerClassName?: string;
}

// Server-side pagination props
export interface ServerPaginationProps {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

// Client-side pagination props
export interface ClientPaginationProps {
  pageSize?: number;
  pageSizeOptions?: number[];
}

// Props for the DataTable component
export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  // For client-side pagination (data is sliced in component)
  pagination?: ClientPaginationProps;
  // For server-side pagination (parent controls pagination)
  serverPagination?: ServerPaginationProps;
  loading?: boolean;
  emptyMessage?: string;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  rowKey: keyof T | ((row: T) => string);
  className?: string;
}

// Skeleton row for loading state
function TableSkeleton({ columns, rows = 5 }: { columns: number; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={rowIndex} className="border-b border-border/50">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td key={colIndex} className="py-3 px-4">
              <div className="h-4 bg-muted rounded animate-pulse" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function DataTable<T>({
  data,
  columns,
  pagination,
  serverPagination,
  loading = false,
  emptyMessage = "No data found.",
  onEdit,
  onDelete,
  rowKey,
  className,
}: DataTableProps<T>) {
  // Client-side pagination state (only used when serverPagination is not provided)
  const defaultPageSize = pagination?.pageSize ?? 10;
  const [clientPage, setClientPage] = useState(1);
  const [clientPageSize, setClientPageSize] = useState(defaultPageSize);

  // Determine if we're using server-side pagination
  const isServerSide = Boolean(serverPagination);

  // Get pagination values based on mode
  const currentPage = isServerSide ? serverPagination!.currentPage : clientPage;
  const pageSize = isServerSide ? serverPagination!.pageSize : clientPageSize;
  const totalItems = isServerSide ? serverPagination!.totalItems : data.length;
  const totalPages = isServerSide ? serverPagination!.totalPages : Math.ceil(data.length / clientPageSize);

  // Get display data - for server-side, use data as-is; for client-side, slice it
  const displayData = useMemo(() => {
    if (isServerSide) {
      // Server already sent paginated data
      return data;
    }
    // Client-side: slice the data
    const startIndex = (clientPage - 1) * clientPageSize;
    return data.slice(startIndex, startIndex + clientPageSize);
  }, [data, isServerSide, clientPage, clientPageSize]);

  // Reset to page 1 when data changes significantly (client-side only)
  useMemo(() => {
    if (!isServerSide) {
      const maxPage = Math.ceil(data.length / clientPageSize);
      if (clientPage > maxPage && maxPage > 0) {
        setClientPage(maxPage);
      } else if (maxPage === 0) {
        setClientPage(1);
      }
    }
  }, [data.length, clientPageSize, clientPage, isServerSide]);

  // Get row key value
  const getRowKey = (row: T): string => {
    if (typeof rowKey === "function") {
      return rowKey(row);
    }
    return String(row[rowKey]);
  };

  // Get cell value
  const getCellValue = (row: T, column: ColumnDef<T>): React.ReactNode => {
    if (column.cell) {
      return column.cell(row);
    }
    if (column.accessorKey) {
      const value = row[column.accessorKey];
      if (value === null || value === undefined) return "-";
      return String(value);
    }
    return "-";
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    if (isServerSide) {
      serverPagination!.onPageChange(page);
    } else {
      setClientPage(page);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (size: number) => {
    if (isServerSide) {
      serverPagination!.onPageSizeChange(size);
    } else {
      setClientPageSize(size);
      setClientPage(1);
    }
  };

  // Check if actions column is needed
  const hasActions = Boolean(onEdit || onDelete);
  const totalColumns = columns.length + (hasActions ? 1 : 0);

  // Determine if pagination should show
  const showPagination = (pagination || serverPagination) && !loading && totalItems > 0;
  const pageSizeOptions = serverPagination?.pageSizeOptions ?? pagination?.pageSizeOptions;

  return (
    <div className={cn("bg-card rounded-xl border border-border", className)}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              {columns.map((column) => (
                <th
                  key={column.id}
                  className={cn(
                    "text-left p-4 font-medium text-muted-foreground text-sm",
                    column.headerClassName
                  )}
                >
                  {column.header}
                </th>
              ))}
              {hasActions && (
                <th className="text-right p-4 font-medium text-muted-foreground text-sm w-[100px]">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <TableSkeleton columns={totalColumns} rows={pageSize} />
            ) : displayData.length === 0 ? (
              <tr>
                <td
                  colSpan={totalColumns}
                  className="text-center py-12 text-muted-foreground"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              displayData.map((row, index) => (
                <tr
                  key={getRowKey(row)}
                  className={cn(
                    "border-b border-border/50 hover:bg-muted/30 transition-colors",
                    index % 2 === 1 && "bg-muted/10"
                  )}
                >
                  {columns.map((column) => (
                    <td
                      key={column.id}
                      className={cn("p-4 text-sm", column.className)}
                    >
                      {getCellValue(row, column)}
                    </td>
                  ))}
                  {hasActions && (
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {onEdit && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => onEdit(row)}
                            aria-label="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {onDelete && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => onDelete(row)}
                            aria-label="Delete"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <DataTablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          pageSizeOptions={pageSizeOptions}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </div>
  );
}
